import { marked } from "marked";
import { jsx } from "slate-hyperscript";

import type { Descendant } from "slate";
import type { EditorValue, RichText } from "./types";

/**
 * Convert an element to Slate node
 */
function parse(el: Element | Node, markAttributes = {}) {
  // Text nodes
  if (el.nodeType === Node.TEXT_NODE) {
    if (el.parentElement?.nodeName == "LI") {
      return el.textContent !== "\n"
        ? jsx("element", { type: "lic" }, el.textContent)
        : undefined;
    }

    if (el.textContent == "\n") {
      return el.parentElement?.nodeName === "BODY"
        ? jsx("fragment")
        : undefined;
    }

    return jsx("text", markAttributes, el.textContent);
  } else if (el.nodeType !== Node.ELEMENT_NODE) {
    return undefined;
  }

  const nodeAttributes: Partial<RichText> = { ...markAttributes };

  // Define attributes for text nodes ("marks")
  switch (el.nodeName) {
    case "U":
      nodeAttributes.underline = true;
      break;
    case "STRONG":
      nodeAttributes.bold = true;
      break;
    case "EM":
      nodeAttributes.italic = true;
      break;
    default:
      break;
  }

  // Recurse children
  const children: (Descendant | undefined)[] = Array.from(el.childNodes)
    .map((node) => parse(node, nodeAttributes))
    .flat()
    .filter(Boolean);

  if (!children.length) return;

  // Build elements
  switch (el.nodeName) {
    case "BODY":
      return jsx("fragment", {}, children);
    case "BR":
      return jsx("element", { type: "p" }, children);
    case "BLOCKQUOTE":
      return jsx("element", { type: "blockquote" }, children);
    case "P":
      return el.parentElement?.nodeName === "BLOCKQUOTE"
        ? jsx("text", {}, el.textContent?.trim())
        : jsx("element", { type: "p" }, children);
    case "OL":
      return jsx("element", { type: "ol" }, children);
    case "UL":
      return jsx("element", { type: "ul" }, children);
    case "LI":
      return jsx("element", { type: "li" }, children);
    default:
      return children;
  }
}

/**
 * Markdown -> Slate
 */
export function deserialize(markdown?: string): EditorValue | undefined {
  if (!markdown || !globalThis.DOMParser) return;

  const html = marked.parse(markdown);

  // Create a DOM parser to parse
  const doc = new DOMParser().parseFromString(html, "text/html");

  return parse(doc.body) as EditorValue;
}
